import classnames from 'classnames';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { iconChevronLeft } from '#src/atoms/icons/system/iconChevronLeft.ts';
import { iconChevronRight } from '#src/atoms/icons/system/iconChevronRight.ts';
import { useTouchScreen } from '#src/hooks/device/useTouchScreen.ts';
import { useIsOverflowing } from '#src/hooks/ui/useIsOverflowing.ts';
import { useScrollPosition } from '#src/hooks/ui/useScrollPosition.ts';
import { glossary } from '#src/lib/glossary.ts';
import { unitMapping, type Unit } from '#src/lib/style.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

// oxlint-disable-next-line sorare/use-horizontal-vertical-layout
const ScrollButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: absolute;
  inset: 0 calc(-1 * var(--double-unit));
  pointer-events: none;
  z-index: 2;
  > * {
    pointer-events: auto;
    &[disabled] {
      visibility: hidden;
    }
  }
  &.contained {
    inset: 0 var(--contained-size);
  }
`;

const Wrapper = styled.div`
  position: relative;
  isolation: isolate;

  &.hideButtonsOnMobile {
    ${ScrollButtonWrapper} {
      visibility: hidden;
    }
  }

  @media ${tabletAndAbove} {
    ${ScrollButtonWrapper} {
      visibility: visible;
    }

    &.displayOnlyOnHover {
      ${ScrollButtonWrapper} {
        visibility: hidden;
      }
      &:hover,
      &:focus-within {
        ${ScrollButtonWrapper} {
          visibility: visible;
        }
      }
    }
  }
`;

// Overflowing item content (e.g. a rotated image) grows scrollWidth past what
// scroll-snap lets the user reach, so check the last item rather than scrollLeft.
const isLastItemInView = (el: HTMLElement) => {
  const visibleItems = Array.from(el.children).filter(
    child => child.clientWidth > 0
  );
  const lastItem = visibleItems.at(-1);
  if (!lastItem) return true;

  return (
    lastItem.getBoundingClientRect().right <=
    el.getBoundingClientRect().right + 1
  );
};

type Props = {
  children: ReactNode;
  wrapperRef: RefObject<HTMLDivElement | null>;
  scrollContainerRef: RefObject<HTMLDivElement | null>;
  contained?: boolean | Unit | string;
  hideButtonsOnMobile?: boolean;
  displayOnlyOnHover?: boolean;
  behavior?: 'smooth' | 'instant' | 'auto';
  itemGap?: Unit;
  overhangSize?: Unit;
  scrollStep?: 'item' | 'container';
  grow?: boolean;
  wrapAround?: boolean;
};

export const WithScrollButtons = ({
  children,
  wrapperRef,
  scrollContainerRef,
  contained,
  hideButtonsOnMobile = false,
  displayOnlyOnHover = true,
  itemGap = 2,
  overhangSize = 0,
  behavior = 'smooth',
  scrollStep = 'container',
  grow = false,
  wrapAround = false,
}: Props) => {
  // update state to force isOverflowing to recompute when the wrapperRef change
  const [, setUpdate] = useState<unknown>();
  const isOverflowing = useIsOverflowing(scrollContainerRef);
  const { scrollPosition } = useScrollPosition(scrollContainerRef);
  const { formatMessage } = useIntl();
  const isTouchScreen = useTouchScreen();

  // Draggable descendants (card images and link wrappers are drag sources)
  // make macOS browsers (Chrome and Safari) treat a trackpad swipe as
  // drag-intent and swallow the wheel deltas instead of scrolling, so
  // translate horizontal wheel to scrollLeft.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return undefined;

    const handleWheel = (event: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;
      if ((event.deltaX < 0 && atStart) || (event.deltaX > 0 && atEnd)) return;

      event.preventDefault();
      el.scrollLeft += event.deltaX;
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [scrollContainerRef]);

  // On IOS safari, the onMouseEnter/focus is triggered by first click. Prevetting the click on caroussel item.
  // https://stackoverflow.com/questions/46034186/ios-safari-click-event-not-triggerd-when-there-is-an-event-listener-on-mouse
  const actualDisplayOnlyOnHover = !isTouchScreen && displayOnlyOnHover;

  const getScrollValue = () => {
    const itemWidth = scrollContainerRef.current?.children[0]?.clientWidth || 0;
    const wrapperWidth = wrapperRef.current?.clientWidth || 0;

    const itemVisibleCount = Math.ceil(
      wrapperWidth / (itemWidth + 8 * itemGap)
    );

    const itemStepWidth = itemWidth + 8 * itemGap;
    const scrollValue =
      scrollStep === 'item'
        ? itemStepWidth
        : itemStepWidth * itemVisibleCount - 8 * overhangSize;
    return scrollValue;
  };

  const getContainedSize = () => {
    if (!contained) {
      return undefined;
    }
    if (typeof contained === 'boolean') {
      return 'var(--unit)';
    }
    if (typeof contained === 'string') {
      return contained;
    }
    return unitMapping[contained];
  };
  return (
    <Wrapper
      ref={wrapperRef}
      onMouseEnter={actualDisplayOnlyOnHover ? setUpdate : undefined}
      className={classnames({
        hideButtonsOnMobile,
        displayOnlyOnHover: actualDisplayOnlyOnHover,
        'w-full': grow,
      })}
    >
      {children}
      {!!isOverflowing && (
        <ScrollButtonWrapper
          className={classnames({
            contained: !!contained,
          })}
          style={
            {
              '--contained-size': getContainedSize(),
            } as CSSProperties
          }
        >
          <IconButton
            icon={iconChevronLeft}
            color="tertiary"
            size="small"
            disabled={scrollPosition === 'start'}
            disableDebounce
            aria-label={formatMessage(glossary.previous)}
            onClick={evt => {
              evt.preventDefault();
              scrollContainerRef.current?.scrollBy({
                left: -getScrollValue(),
                behavior,
              });
            }}
          />
          <IconButton
            icon={iconChevronRight}
            color="tertiary"
            size="small"
            disabled={!wrapAround && scrollPosition === 'end'}
            disableDebounce
            aria-label={formatMessage(glossary.next)}
            onClick={evt => {
              evt.preventDefault();
              const el = scrollContainerRef.current;
              if (!el) return;

              if (wrapAround && isLastItemInView(el)) {
                el.scrollTo({ left: 0, behavior });
                return;
              }

              el.scrollBy({ left: getScrollValue(), behavior });
            }}
          />
        </ScrollButtonWrapper>
      )}
    </Wrapper>
  );
};
